/**

_StoreProvider_: a wrapper component that provides a Store's current state as `props` to a child component.


Using this wrapper has some distinct advantages over the child component itself manually piping from the
store into its own internal `state`:

  1) Less machinery to implement in the child component (subscribe/unsubscribe in lifecycle methods, local state, etc.)

  2) The expected props can be documented and validated in propTypes, making the component's data dependency
     structure more clear and easier to debug.

  3) The component is decoupled from any specific Store instance, so it can be more easily reused across
     projects or with multiple stores in the same project, and can be tested without the presence of any store.


Accepts two `props`:

  - `store` (required): the Store object to subscribe to. This is currently expected to be a Reflux store, or
        an object that implements the `listen`/`getInitialState` methods in the same way as a Reflux store.

  - `mapping`: an optional mapping of the store state object to the child component's props. If not specified,
        the store's state structure will be passed unchanged. Can be:
          - a function that is passed the store state and returns the props
          - an array of store property names that will be copied to the props as the same name
          - a string that will be the sole property name copied to the props
          - an object whose keys are the store property names and values are the corresponding props names

Always expects exactly one child which is the component to which it will pass the store's state as `props`.


Examples:

    <StoreProvider store={ MyStore }>
      <MyComponent className="oogly" />
    </StoreProvider>

In this example, if MyStore provides state `{isLoading: false, data: []}` then MyComponent will receive
`isLoading` and `data` in its props.

You can also nest StoreProviders if the child component consumes data from more than one store; the props
from each will be combined and passed down. In this case you may want to implement custom `mapping`s
to keep the props from colliding:

    <StoreProvider store={ MyFirstStore } mapping={ state => ({store1: state}) }>
      <StoreProvider store={ MySecondStore } mapping={ {loading: 'store2IsLoading', data: 'store2Data'} }>
        <MyComponent />
      </StoreProvider>
    </StoreProvider>


In general, it should be safe to deeply nest StoreProviders that attach to the same store, which is nice
when a store holds common app state needed by many components throughout the app that would be tedious
to pass down everywhere in `props`. In this case only the topmost StoreProvider for each store will actually
attach a listener; all descendants will have the store state passed down via React `context` to avoid cases
where a descendant might react to an update even though it will be destroyed in the same render pass anyway.
It's possible this could sometimes cause a descendant to not update when the store changes, e.g. if an
intermediate parent's `shouldComponentUpdate` cancels child rendering, but that's a pattern to avoid anyway.

*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import {Iterable} from 'immutable'

const CONTEXT_SHAPE = {
  storeProviderStates: T.arrayOf(T.shape({ store: T.object, state: T.object }))
}

class StoreProvider extends React.Component {
  static displayName = 'StoreProvider'

  static propTypes = {
    store: T.shape({
      listen: T.func.isRequired,
      getInitialState: T.func
    }).isRequired,
    mapping: T.oneOfType([
      T.func,
      T.string,
      T.arrayOf(T.string),
      T.objectOf(T.string)
    ])
  }

  static contextTypes = CONTEXT_SHAPE

  static childContextTypes = CONTEXT_SHAPE

  constructor(props) {
    super(props)
    this.state = {
      storeState: null
    }
  }

  getChildContext() {
    // Add the store's state to the context collection if it isn't already there
    let contextStates = this.context.storeProviderStates
    contextStates = contextStates ? contextStates.slice() : []
    if (
      !contextStates.length ||
      !_.find(contextStates, ({ store }) => store === this.props.store)
    ) {
      contextStates.push({
        store: this.props.store,
        state: this.state.storeState
      })
    }
    return {
      storeProviderStates: contextStates
    }
  }

  UNSAFE_componentWillMount() {
    // Only subscribe to the store if there isn't already an ancestor StoreProvider subscribing
    // to the same store. This prevents setState being called for both an ancestor and descendant
    // when the ancestor's rerender might result in the descendant going away, which errors out.
    if (!this._getStoreStateFromContext()) {
      const store = this.props.store
      if (_.isFunction(store.listen)) {
        this._unsub = store.listen(this._onStoreStateUpdate)
        const getInitial = store.getInitialState || store.getDefaultData //try getDefaultData for compat with bacon-reflux stores
        if (_.isFunction(getInitial)) {
          this._onStoreStateUpdate(getInitial.call(store))
        }
      }
    }
  }

  componentWillUnmount() {
    if (this._unsub) {
      this._unsub()
    }
  }

  _onStoreStateUpdate = (storeState) => {
    this.setState({ 
      storeState: Iterable.isIterable(storeState) ? storeState : _.clone(storeState) 
    })
  }

  _getStoreStateFromContext = () => {
    const parentStates = this.context.storeProviderStates
    return (
      (parentStates &&
        _.find(parentStates, ({ store }) => store === this.props.store)) ||
      null
    )
  }

  render() {
    const { props, state } = this
    const mapping = props.mapping

    const stateFromContext = this._getStoreStateFromContext()
    let childProps = stateFromContext
      ? stateFromContext.state
      : state.storeState

    if (mapping) {
      if (_.isFunction(mapping)) {
        childProps = mapping(childProps)
      } else if (_.isString(mapping) || _.isArray(mapping)) {
        childProps = _.pick(childProps, mapping)
      } else if (_.isObject(mapping)) {
        const out = {}
        _.forOwn(mapping, (propName, storeName) => {
          out[propName] = childProps[storeName]
        })
        childProps = out
      }
    }

    childProps = _.assign(
      _.omit(props, 'store', 'mapping', 'children'),
      childProps
    )

    return React.cloneElement(React.Children.only(props.children), childProps)
  }
}

export default StoreProvider


/**
 * Higher-order component wrapper for <StoreProvider/> - this can be more ergonomic than
 * the React parent element version when wrapping a whole component.
 * @param {React.Component} TargetCmp - the component class to be wrapped
 * @param {Object|Array<{store,mapping}>} store - either a single store instance, or an array
 *        of multiple objects each specifying a store plus optional mapping, as a convenience
 *        to avoid nesting StoreProviderHOC calls when binding to multiple stores.
 * @param {Function|String|Array<String>|Object} mapping - see docs for StoreProvider above -
 *        only relevant when `store` is not an array
 */
export function StoreProviderHOC(TargetCmp, store, mapping) {
  // Handle multi-store array variant
  if (Array.isArray(store)) {
    let cmp = TargetCmp
    for (let i = store.length; i--;) {
      cmp = StoreProviderHOC(cmp, store[i].store, store[i].mapping)
    }
    return cmp
  }

  const Wrapper = props =>
    <StoreProvider {...props} store={store} mapping={mapping}>
      <TargetCmp />
    </StoreProvider>
  Wrapper.displayName = `StoreProvider(${TargetCmp.displayName || 'Component'})`
  return Wrapper
}
